import { Type, type Static } from '@sinclair/typebox';
export interface BuyCostImportError {
  row: number;
  sku: string;
  asin: string;
  startDateString?: string;
  errorMessage: BuyCostImportErrorMessage
}

export enum BuyCostImportErrorMessage {
  DUPLICATE = "Duplicate SKU or ASIN",
  INVALID_SKU = "Invalid or empty SKU",
  INVALID_ASIN = "Invalid or empty ASIN",
  INVALID_DATE = "Invalid buy cost start date",
  INVALID_COST = "Invalid buy cost value"
}

export interface BuyCostParserOutput {
  buyCosts: BuyCostImportSchema[];
  errors?: BuyCostImportError[]
}

interface BuyCostSoftwareDetail {
  name: string;
  docs: string;
  dateFormat: string;
}

export enum BuyCostSoftwareType {
  BOXEM = 'BOXEM',
  GOAURA = 'GOAURA',
  BQOOL = 'BQOOL',
  INVENTORY_LAB = 'INVENTORY_LAB',
  SELLERBOARD = 'SELLERBOARD',
}

export type BuyCostSoftware = keyof typeof BuyCostSoftwareType;

type BuyCostSoftwareConfig = Record<BuyCostSoftwareType, BuyCostSoftwareDetail>;

export const BuyCostSoftwareConfig: BuyCostSoftwareConfig = {
  [BuyCostSoftwareType.BOXEM]: {
    name: 'Boxem',
    docs: 'https://www.boxem.com/help/importing-buy-costs-from-boxem',
    dateFormat: 'MM/DD/YYYY',
  },
  [BuyCostSoftwareType.GOAURA]: {
    name: 'Aura',
    docs: 'https://www.boxem.com/help/importing-buy-costs-from-aura',
    dateFormat: 'MM/DD/YYYY',
  },
  [BuyCostSoftwareType.BQOOL]: {
    name: 'BQool',
    docs: 'https://www.boxem.com/help/importing-buy-costs-from-bqool',
    dateFormat: 'M/D/YYYY',
  },
  [BuyCostSoftwareType.INVENTORY_LAB]: {
    name: 'InventoryLab',
    docs: 'https://www.boxem.com/help/importing-buy-costs-from-inventorylab',
    dateFormat: 'MM/DD/YYYY',
  },
  [BuyCostSoftwareType.SELLERBOARD]: {
    name: 'Sellerboard',
    docs: 'https://www.boxem.com/help/importing-buy-costs-from-sellerboard',
    dateFormat: 'M/D/YYYY',
  }
};

export const BuyCostByPeriod = Type.Record(Type.String(), Type.Union([Type.String({ format: 'date-time' }), Type.Null()]))

export type BuyCostByPeriod = Static<typeof BuyCostByPeriod>;

export const BuyCostImportSchema = Type.Object({
  sku: Type.String(), // 'sku' is a string
  asin: Type.String(),
  cost: Type.String(),
  period: Type.Union([Type.String({ format: 'date-time', nullable: true }), Type.Null()])
});

export type BuyCostImportSchema = Static<typeof BuyCostImportSchema>;

export const BuyCostImportRequestBody = Type.Object(
  {
    software: Type.Enum(BuyCostSoftwareType),
    buyCosts: Type.Array(BuyCostImportSchema)
  }
);

export type BuyCostImportRequestBody = Static<typeof BuyCostImportRequestBody>;

export const BuyCostImportResponse = Type.Object({
  message: Type.String(),
  count: Type.Number(),
});

export type BuyCostImportResponse = Static<typeof BuyCostImportResponse>;

export const CostCategorySchema = Type.Object({
  costCategoryName: Type.String(),  // Name of the cost category
  total: Type.Number(),            // Total amount for the cost category
  description: Type.String(),
});

// Define the schema for each buy cost entry
export const BuyCostEntrySchema = Type.Object({
  total: Type.Number(),                          // Total for the buy cost entry
  datePurchased: Type.Optional(Type.String({ format: 'date' })), // Date of purchase
  costCategories: Type.Array(CostCategorySchema), // Array of cost categories
});

export type BuyCostEntry = Static<typeof BuyCostEntrySchema>;


export const VariableBuyCostBody = Type.Object({
  buyCosts: Type.Array(BuyCostEntrySchema),
});

export type VariableBuyCostBody = Static<typeof VariableBuyCostBody>;

export const GetVariableBuyCostResponse = Type.Object({
  listingId: Type.Number(),
  buyCosts: Type.Array(BuyCostEntrySchema),
});

export type GetVariableBuyCostResponse = Static<typeof GetVariableBuyCostResponse>;

export const GetUserCategoryResponse = Type.Object({
  userCategories: Type.Array(Type.String())
})

export type GetUserCategoryResponse = Static<typeof GetUserCategoryResponse>;